import * as React from 'react';
import MainLayout from '../../components/Layout/MainLayout.tsx';
import RangeDatePicker from '../../components/Layout/DatePicker.tsx';
import 'react-datepicker/dist/react-datepicker.css';
import '../../components/Layout/DatePicker.css';
import { CopyIcon, HashIcon, CheckIcon } from '../../components/icons/Icons.tsx';
import { 
  Send, Download, Filter, RefreshCw, CheckCircle, ArrowRight, 
  AlertTriangle, Clock, DollarSign, Users, FileText, Shield, 
  X, Upload, Calendar, ArrowLeft, TrendingUp, TrendingDown, 
  Target, Briefcase, Globe, Activity, Gift, CreditCard, File, Inbox
} from 'react-feather';
import { partnersData } from '../../data/partnersData.ts';

// Transaction interface
interface Transaction {
  id: string;
  transactionDate: string;
  partner: string;
  product: 'Ads Postpaid';
  type: string;
  amount: number;
  currency: string;
  status: 'Paid' | 'Pending' | 'Overdue' | 'Voided';
  location?: string;
  logoUrl?: string | null;
  category?: 'Invoice' | 'Payment' | 'Adjustment';
  reference?: string;
  description?: string;
  reason?: string;
  dueDate?: string;
  invoiceNumber?: string;
  billingPeriod?: string;
  approvedBy?: string;
}

// DropdownFilterProps interface
interface DropdownFilterProps {
  label: string;
  options: string[];
  selectedValue: string;
  onChange: (value: string) => void;
}

// Helper functions
// Function to generate random ISO date string
const getRandomISODate = (baseDate: string, index: number): string => {
  const date = new Date(baseDate);
  const hour = Math.floor(Math.random() * 24);
  const minute = Math.floor(Math.random() * 60);
  const second = Math.floor(Math.random() * 60);
  date.setUTCHours(hour, minute, second, 0);
  return date.toISOString();
};

// Function to generate a simple UUID v4
const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// Helper function to determine transaction category
const getTransactionCategoryBase = (transaction: Pick<Transaction, 'type'>): 'Invoice' | 'Payment' | 'Adjustment' => {
  if (transaction.type.toLowerCase().includes('adjustment')) return 'Adjustment';
  if (transaction.type.toLowerCase().includes('payment') || transaction.type.toLowerCase().includes('paid')) return 'Payment';
  return 'Invoice';
};

// Helper function to add days to a date
const addDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

// Filter partners relevant to Ads Postpaid
const adsPartners = partnersData.filter(p =>
  p.products.includes('Postpaid Ads') || ['Google Ads', 'Meta Ads', 'TikTok', 'Snapchat', 'Twitter', 'LinkedIn', 'Pinterest', 'Reddit', 'Taboola', 'Outbrain', 'Spotify', 'Amazon Ads', 'Microsoft Ads', 'YouTube', 'Apple Search Ads'].includes(p.name)
);

// If adsPartners is empty, fallback to using all partnersData
const availablePartners = adsPartners.length > 0 ? adsPartners : partnersData;

// *** Shuffle the availablePartners for better variety ***
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]; // Swap elements
  }
  return shuffled;
}
const shuffledPartners = shuffleArray(availablePartners);

// Generate postpaid-specific mock data
const rawMockTransactionsBase: Omit<Transaction, 'id' | 'location' | 'logoUrl' | 'product' | 'partner'>[] = [
  // Invoices
  { transactionDate: '2025-04-01', type: 'Monthly Invoice', amount: 450000.00, currency: 'AED', status: 'Paid', dueDate: '2025-04-15', invoiceNumber: 'INV-2025-04-001', billingPeriod: 'Mar 2025' },
  { transactionDate: '2025-04-01', type: 'Monthly Invoice', amount: 320000.00, currency: 'SAR', status: 'Paid', dueDate: '2025-04-15', invoiceNumber: 'INV-2025-04-002', billingPeriod: 'Mar 2025' },
  { transactionDate: '2025-04-01', type: 'Monthly Invoice', amount: 180000.00, currency: 'AED', status: 'Paid', dueDate: '2025-04-15', invoiceNumber: 'INV-2025-04-003', billingPeriod: 'Mar 2025' },
  { transactionDate: '2025-04-01', type: 'Monthly Invoice', amount: 270000.00, currency: 'SAR', status: 'Overdue', dueDate: '2025-04-15', invoiceNumber: 'INV-2025-04-004', billingPeriod: 'Mar 2025' },
  
  // Payments
  { transactionDate: '2025-04-10', type: 'Payment Received', amount: -450000.00, currency: 'AED', status: 'Paid', reference: 'PAY-0042351' },
  { transactionDate: '2025-04-12', type: 'Payment Received', amount: -320000.00, currency: 'SAR', status: 'Paid', reference: 'PAY-0042352' },
  { transactionDate: '2025-04-14', type: 'Payment Received', amount: -180000.00, currency: 'AED', status: 'Paid', reference: 'PAY-0042353' },
  
  // May invoices
  { transactionDate: '2025-05-01', type: 'Monthly Invoice', amount: 510000.00, currency: 'AED', status: 'Pending', dueDate: '2025-05-15', invoiceNumber: 'INV-2025-05-001', billingPeriod: 'Apr 2025' },
  { transactionDate: '2025-05-01', type: 'Monthly Invoice', amount: 380000.00, currency: 'SAR', status: 'Pending', dueDate: '2025-05-15', invoiceNumber: 'INV-2025-05-002', billingPeriod: 'Apr 2025' },
  { transactionDate: '2025-05-01', type: 'Monthly Invoice', amount: 220000.00, currency: 'AED', status: 'Pending', dueDate: '2025-05-15', invoiceNumber: 'INV-2025-05-003', billingPeriod: 'Apr 2025' },
  { transactionDate: '2025-05-01', type: 'Monthly Invoice', amount: 150000.00, currency: 'QAR', status: 'Pending', dueDate: '2025-05-15', invoiceNumber: 'INV-2025-05-004', billingPeriod: 'Apr 2025' },
  
  // Adjustments
  { transactionDate: '2025-04-20', type: 'Credit Adjustment', amount: -30000.00, currency: 'AED', status: 'Paid', reason: 'Service outage compensation' },
  { transactionDate: '2025-04-22', type: 'Fee Adjustment', amount: 15000.00, currency: 'AED', status: 'Pending', reason: 'Late payment fee' },
  
  // Additional transactions for variety
  { transactionDate: '2025-03-01', type: 'Monthly Invoice', amount: 420000.00, currency: 'AED', status: 'Paid', dueDate: '2025-03-15', invoiceNumber: 'INV-2025-03-001', billingPeriod: 'Feb 2025' },
  { transactionDate: '2025-03-01', type: 'Monthly Invoice', amount: 280000.00, currency: 'SAR', status: 'Paid', dueDate: '2025-03-15', invoiceNumber: 'INV-2025-03-002', billingPeriod: 'Feb 2025' },
  { transactionDate: '2025-03-12', type: 'Payment Received', amount: -420000.00, currency: 'AED', status: 'Paid', reference: 'PAY-0041235' },
  { transactionDate: '2025-03-14', type: 'Payment Received', amount: -280000.00, currency: 'SAR', status: 'Paid', reference: 'PAY-0041236' },
  { transactionDate: '2025-02-01', type: 'Monthly Invoice', amount: 390000.00, currency: 'AED', status: 'Paid', dueDate: '2025-02-15', invoiceNumber: 'INV-2025-02-001', billingPeriod: 'Jan 2025' },
  { transactionDate: '2025-02-11', type: 'Payment Received', amount: -390000.00, currency: 'AED', status: 'Paid', reference: 'PAY-0040125' },
];

// Process raw transactions specifically for Ads Postpaid
const processedAdsTransactions: Transaction[] = rawMockTransactionsBase
  .map((tx, index) => {
    // Assign a partner cyclically from the *shuffled* list
    const partnerInfo = shuffledPartners[index % shuffledPartners.length];
    
    // For payments, match them to corresponding invoices for the same partner
    const category = getTransactionCategoryBase(tx);
    
    return {
      ...tx,
      id: generateUUID(),
      product: 'Ads Postpaid',
      partner: partnerInfo.name, // Get partner name from data
      transactionDate: getRandomISODate(tx.transactionDate, index),
      location: partnerInfo.location || 'N/A', // Get location from data
      logoUrl: partnerInfo.logoUrl, // Get logoUrl from data
      category: category,
    } as Transaction;
  });

// *** Add ~150 more Pending Invoices ***
const additionalPendingInvoices: Transaction[] = [];
const basePendingDate = new Date('2025-06-01T00:00:00Z');
const currencies = ['AED', 'SAR', 'QAR'];

for (let i = 0; i < 150; i++) {
  const partnerInfo = shuffledPartners[i % shuffledPartners.length];
  const transactionDate = new Date(basePendingDate);
  transactionDate.setDate(basePendingDate.getDate() + Math.floor(i / 10)); // Spread dates slightly
  const dueDate = addDays(transactionDate, 15); // Due 15 days later
  const billingMonth = transactionDate.toLocaleDateString('en-US', { month: 'short' });
  const billingYear = transactionDate.getFullYear();
  const randomAmount = Math.floor(Math.random() * (600000 - 100000 + 1)) + 100000; // Random amount between 100k and 600k

  additionalPendingInvoices.push({
    id: generateUUID(),
    transactionDate: getRandomISODate(transactionDate.toISOString().split('T')[0], i),
    partner: partnerInfo.name,
    product: 'Ads Postpaid',
    type: 'Monthly Invoice',
    amount: randomAmount,
    currency: currencies[i % currencies.length],
    status: 'Pending',
    location: partnerInfo.location || 'N/A',
    logoUrl: partnerInfo.logoUrl,
    category: 'Invoice',
    dueDate: dueDate.toISOString(),
    invoiceNumber: `INV-${billingYear}-${billingMonth.toUpperCase()}-${String(i + 1).padStart(3, '0')}`,
    billingPeriod: `${billingMonth} ${billingYear}`,
  } as Transaction);
}

// Combine and sort all transactions
const allTransactions = [...processedAdsTransactions, ...additionalPendingInvoices]
  .sort((a, b) => new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime());

// Helper function for currency formatting
const formatCurrency = (amount: number, currency: string): string => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return formatter.format(Math.abs(amount));
};

// Helper to get partner initials
const getInitials = (name: string): string => {
  if (!name) return '??';
  const words = name.split(' ').filter(Boolean);
  if (words.length === 0) return '??';
  if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
  const firstInitial = words[0][0];
  const lastInitial = words[words.length - 1][0];
  return (firstInitial + lastInitial).toUpperCase();
};

// Helper function to format date and time
const formatDateTime = (isoString: string): { date: string, time: string } => {
  try {
    const dateObj = new Date(isoString);
    const formattedDate = dateObj.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
    const formattedTime = dateObj.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
    return { date: formattedDate, time: formattedTime };
  } catch (error) {
    console.error("Error formatting date:", isoString, error);
    return { date: 'Invalid Date', time: '' };
  }
};

// DropdownFilter Component
const DropdownFilter: React.FC<DropdownFilterProps> = ({ label, options, selectedValue, onChange }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [hoveredOption, setHoveredOption] = React.useState<string | null>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  const handleSelect = (option: string) => {
    onChange(option);
    setIsOpen(false);
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div style={{ position: 'relative' }} ref={dropdownRef}>
      <button
        style={{
          padding: '8px 12px',
          borderRadius: '6px',
          border: '1px solid #d1d5db',
          backgroundColor: 'white',
          fontSize: '14px',
          color: '#374151',
          cursor: 'pointer',
          minWidth: '140px',
          textAlign: 'left',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '6px',
          transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
        }}
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={(e) => e.currentTarget.style.borderColor = '#adb5bd'}
        onMouseLeave={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
      >
        {label}: <span style={{ fontWeight: '500', color: '#1f2937' }}>{selectedValue}</span>
        <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="#6b7280" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d={isOpen ? "m18 14-6-6-6 6" : "m6 10 6 6 6-6"} />
        </svg>
      </button>
      {isOpen && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)', zIndex: 10, minWidth: '160px', padding: '4px 0' }}>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: '200px', overflowY: 'auto' }}>
            {options.map(option => (
              <li
                key={option}
                style={{
                  padding: '8px 16px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#374151',
                  backgroundColor: hoveredOption === option ? '#f9fafb' : 'white'
                }}
                onClick={() => handleSelect(option)}
                onMouseEnter={() => setHoveredOption(option)}
                onMouseLeave={() => setHoveredOption(null)}
              >
                {option}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// Status Badge Component
const StatusBadge: React.FC<{ status: Transaction['status'] }> = ({ status }) => {
  let styles: React.CSSProperties = {
    padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 500,
    textTransform: 'capitalize', display: 'inline-block',
  };
  switch (status) {
    case 'Paid': styles = { ...styles, backgroundColor: '#ecfdf5', color: '#059669' }; break;
    case 'Pending': styles = { ...styles, backgroundColor: '#fefce8', color: '#d97706' }; break;
    case 'Overdue': styles = { ...styles, backgroundColor: '#fef2f2', color: '#dc2626' }; break;
    case 'Voided': styles = { ...styles, backgroundColor: '#f8fafc', color: '#64748b' }; break;
    default: styles = { ...styles, backgroundColor: '#f8fafc', color: '#64748b' }; break;
  }
  return <span style={styles}>{status}</span>;
};

// Category Badge Component
const CategoryBadge: React.FC<{ category: 'Invoice' | 'Payment' | 'Adjustment' }> = ({ category }) => {
  let styles: React.CSSProperties = {
    borderRadius: '12px', fontSize: '12px', fontWeight: 500,
    textTransform: 'capitalize', display: 'inline-block',
  };
  switch (category) {
    case 'Invoice': styles = { ...styles, color: '#ef4444' }; break;
    case 'Payment': styles = { ...styles, color: '#059669' }; break;
    case 'Adjustment': styles = { ...styles, color: '#4f46e5' }; break;
    default: styles = { ...styles, color: '#64748b' }; break;
  }
  return <span style={styles}>{category}</span>;
};

// Transaction Details Panel Component
interface TransactionDetailsPanelProps {
  transaction: Transaction | null;
  onClose: () => void;
  isVisible: boolean;
}

const TransactionDetailsPanel: React.FC<TransactionDetailsPanelProps> = ({ transaction, onClose, isVisible }) => {
  const panelRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if ((event.target as Element).closest('[data-transaction-row="true"]')) return;
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) onClose();
    };
    if (isVisible) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isVisible, onClose]);

  if (!transaction) return null;

  const dateTime = formatDateTime(transaction.transactionDate);
  const category = transaction.category || getTransactionCategoryBase(transaction);
  const isPaid = transaction.status === 'Paid';
  const isInvoice = category === 'Invoice';
  const dueDate = transaction.dueDate ? formatDateTime(transaction.dueDate).date : null;

  return (
    <div
      ref={panelRef}
      style={{
        position: 'fixed', top: '64px', right: 0, width: '380px', height: 'calc(100vh - 64px)',
        backgroundColor: 'white', boxShadow: '-4px 0 10px rgba(0, 0, 0, 0.1)', zIndex: 1000,
        overflowY: 'auto', transform: isVisible ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.2s ease-out', padding: '24px',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#111827' }}>
          {isInvoice ? 'Invoice Details' : category === 'Payment' ? 'Payment Details' : 'Adjustment Details'}
        </h2>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background-color 0.2s' }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <X size={20} />
        </button>
      </div>

      {/* Content */}
      <div>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
          <StatusBadge status={transaction.status} />
          {category === 'Adjustment' && <span style={{ backgroundColor: '#eef2ff', color: '#4f46e5', padding: '6px 12px', borderRadius: '16px', fontSize: '14px', fontWeight: 500 }}>Adjustment</span>}
        </div>

        <div style={{ marginBottom: '32px' }}>
          <p style={{ color: '#6b7280', fontSize: '16px', margin: '0 0 8px 0', fontWeight: 500 }}>Amount</p>
          <h3 style={{ fontSize: '32px', fontWeight: 600, margin: 0, color: category === 'Payment' ? '#047857' : '#dc2626' }}>
            {formatCurrency(transaction.amount, transaction.currency)}
          </h3>
        </div>

        <div style={{ height: '1px', backgroundColor: '#e5e7eb', margin: '24px 0' }}></div>

        <div style={{ marginBottom: '16px' }}>
          <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0' }}>Partner</p>
          <div style={{ color: '#111827', fontSize: '15px', fontWeight: 500 }}>{transaction.partner}</div>
        </div>
        <div style={{ marginBottom: '16px' }}>
          <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0' }}>Date & Time</p>
          <div style={{ color: '#111827', fontSize: '15px', fontWeight: 500 }}>{dateTime.date}, {dateTime.time}</div>
        </div>
        {isInvoice && dueDate && (
          <div style={{ marginBottom: '16px' }}>
            <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0' }}>Due Date</p>
            <div style={{ color: '#111827', fontSize: '15px', fontWeight: 500 }}>{dueDate}</div>
          </div>
        )}
        {isInvoice && transaction.billingPeriod && (
          <div style={{ marginBottom: '16px' }}>
            <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0' }}>Billing Period</p>
            <div style={{ color: '#111827', fontSize: '15px', fontWeight: 500 }}>{transaction.billingPeriod}</div>
          </div>
        )}
        {isInvoice && transaction.invoiceNumber && (
          <div style={{ marginBottom: '16px' }}>
            <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0' }}>Invoice Number</p>
            <div style={{ color: '#111827', fontSize: '15px', fontWeight: 500 }}>{transaction.invoiceNumber}</div>
          </div>
        )}
        {category === 'Payment' && transaction.reference && (
          <div style={{ marginBottom: '16px' }}>
            <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0' }}>Payment Reference</p>
            <div style={{ color: '#111827', fontSize: '15px', fontWeight: 500 }}>{transaction.reference}</div>
          </div>
        )}
        <div style={{ marginBottom: '16px' }}>
          <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0' }}>Type</p>
          <div style={{ color: '#111827', fontSize: '15px', fontWeight: 500 }}>{transaction.type}</div>
        </div>
        {transaction.reason && (
          <div style={{ marginBottom: '16px' }}>
            <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 4px 0' }}>Reason</p>
            <div style={{ color: '#111827', fontSize: '15px', fontWeight: 500 }}>{transaction.reason}</div>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', marginTop: '40px', color: '#9ca3af', fontSize: '14px' }}>
          <Clock size={16} style={{ marginRight: '6px' }} />
          Transaction ID: {transaction.id.substring(0, 8)}
        </div>
      </div>
    </div>
  );
};

// Metric Widget Component
interface MetricWidgetProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  secondaryValue?: string;
  secondaryColor?: string;
  bgColor?: string;
  iconColor?: string;
}

const MetricWidget: React.FC<MetricWidgetProps> = ({ 
  title, 
  value, 
  icon,
  secondaryValue, 
  secondaryColor = '#10b981',
  bgColor = '#f0fdf4',
  iconColor = '#10b981'
}) => {
  const renderIcon = () => {
    if (React.isValidElement(icon)) {
      try {
        return React.cloneElement(icon as React.ReactElement<{ size?: number; color?: string }>, { size: 18, color: iconColor });
      } catch (e) {
        console.error("Could not clone icon element:", e);
        return icon;
      }
    } else if (typeof icon === 'function') {
       const IconComponent = icon as React.ElementType;
       return <IconComponent size={18} color={iconColor} />;
    }
    return null;
  };

  return (
    <div style={{ 
      backgroundColor: 'white', 
      borderRadius: '12px', 
      padding: '20px', 
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
      border: '1px solid #f3f4f6',
      display: 'flex',
      flexDirection: 'column',
      height: '100%'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
        <div style={{ 
          width: '36px', 
          height: '36px', 
          borderRadius: '50%', 
          backgroundColor: bgColor, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          marginRight: '12px'
        }}>
          {renderIcon()}
        </div>
        <h3 style={{ fontSize: '14px', fontWeight: 500, color: '#6b7280', margin: 0 }}>{title}</h3>
      </div>
      
      <div style={{ marginBottom: secondaryValue ? '8px' : 0 }}>
        <h2 style={{ fontSize: '24px', fontWeight: 700, margin: '0', color: '#111827' }}>
          {value}
        </h2>
      </div>
      
      {secondaryValue && (
        <div style={{ fontSize: '14px', fontWeight: 500, color: secondaryColor }}>
          {secondaryValue}
        </div>
      )}
    </div>
  );
};

// Main Balance Widget Component
interface MainBalanceWidgetProps {
  title: string;
  totalBalance: number;
  change: number;
  currency: string;
  icon?: React.ReactNode;
  isNegative?: boolean;
}

const MainBalanceWidget: React.FC<MainBalanceWidgetProps> = ({ 
  title = "Current Balance",
  totalBalance, 
  change, 
  currency, 
  icon = <CreditCard />,
  isNegative = false
}) => {
  const trend = change >= 0 ? 'up' : 'down';
  const changeText = `${change >= 0 ? '+' : ''}${formatCurrency(change, currency)}`;
  const changeColor = trend === 'up' ? '#10b981' : '#ef4444';
  
  const defaultBgColor = isNegative ? '#fee2e2' : '#f0fdf4';
  const defaultIconColor = isNegative ? '#ef4444' : '#10b981';
  const balanceColor = isNegative ? '#dc2626' : '#111827';

  const renderIcon = () => {
    if (React.isValidElement(icon)) {
      try {
        return React.cloneElement(icon as React.ReactElement<{ size?: number; color?: string }>, { size: 18, color: defaultIconColor });
      } catch (e) {
        console.error("Could not clone icon element:", e);
        return icon; 
      }
    } else if (typeof icon === 'function') {
       const IconComponent = icon as React.ElementType;
       return <IconComponent size={18} color={defaultIconColor} />;
    }
    return null; 
  };

  return (
    <div style={{ 
      backgroundColor: 'white', 
      borderRadius: '12px', 
      padding: '20px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
      border: '1px solid #f3f4f6',
      display: 'flex',
      flexDirection: 'column',
      height: '100%'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
        <div style={{ 
          width: '36px', 
          height: '36px', 
          borderRadius: '50%', 
          backgroundColor: defaultBgColor, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          marginRight: '12px'
        }}>
          {renderIcon()}
        </div>
        <h3 style={{ fontSize: '14px', fontWeight: 500, color: '#6b7280', margin: 0 }}>{title}</h3>
      </div>
      
      <div style={{ marginBottom: '8px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: 700, margin: '0', color: balanceColor }}>
          {formatCurrency(Math.abs(totalBalance), currency)}
        </h2>
      </div>
      
      <div style={{ 
        display: 'flex', 
        alignItems: 'center',
        fontSize: '14px', 
        fontWeight: 500,
        color: changeColor
      }}>
        {trend === 'up' ? <TrendingUp size={16} style={{ marginRight: '4px' }} /> : 
         <TrendingDown size={16} style={{ marginRight: '4px' }} />}
        {changeText}
      </div>
    </div>
  );
};

// Main AdsPostpaidWalletPage component
const AdsPostpaidWalletPage: React.FC = () => {
  const [transactions, setTransactions] = React.useState<Transaction[]>(allTransactions);
  const [hoveredRowIndex, setHoveredRowIndex] = React.useState<number | null>(null);
  const [searchTerm, setSearchTerm] = React.useState<string>('');
  const [hoveredIdIcon, setHoveredIdIcon] = React.useState<string | null>(null);
  const [copiedId, setCopiedId] = React.useState<string | null>(null);

  // Filter States
  const [selectedDateRange, setSelectedDateRange] = React.useState<string>('All Time');
  const [selectedType, setSelectedType] = React.useState<string>('All Types');
  const [selectedCountry, setSelectedCountry] = React.useState<string>('All Countries');
  const [selectedStatus, setSelectedStatus] = React.useState<string>('All Statuses');
  const [customStartDate, setCustomStartDate] = React.useState<Date | null>(null);
  const [customEndDate, setCustomEndDate] = React.useState<Date | null>(null);
  const [isDatePickerVisible, setIsDatePickerVisible] = React.useState<boolean>(false);

  // Selected transaction state for details panel
  const [selectedTransaction, setSelectedTransaction] = React.useState<Transaction | null>(null);
  const [isPanelVisible, setIsPanelVisible] = React.useState<boolean>(false);

  // Filter Options
  const typeOptions = React.useMemo(
    () => ['All Types', ...Array.from(new Set(transactions.map(tx => tx.type))).sort()],
    [transactions]
  );
  const countryOptions = React.useMemo(
    () => ['All Countries', ...Array.from(new Set(transactions.map(tx => tx.location || 'N/A'))).filter(Boolean).sort()],
    [transactions]
  );
  const statusOptions = React.useMemo(() => ['All Statuses', 'Paid', 'Pending', 'Overdue', 'Voided'], []);
  const dateRangeOptions = ['All Time', 'Last 7 Days', 'Last 30 Days', 'Last 90 Days', 'Custom date'];

  // Filtering Logic
  const filteredTransactions = React.useMemo(() => {
    return transactions.filter(tx => {
      const txDate = new Date(tx.transactionDate);

      // Date Range Filter
      let dateMatch = true;
      if (selectedDateRange === 'Custom date') {
        if (customStartDate && customEndDate) {
          const adjustedEndDate = new Date(customEndDate);
          adjustedEndDate.setHours(23, 59, 59, 999);
          dateMatch = txDate >= customStartDate && txDate <= adjustedEndDate;
        } else if (customStartDate) {
          dateMatch = txDate >= customStartDate;
        } else {
          dateMatch = true;
        }
      } else if (selectedDateRange === 'Last 7 Days') {
        const startDate = new Date(); startDate.setDate(startDate.getDate() - 7); dateMatch = txDate >= startDate;
      } else if (selectedDateRange === 'Last 30 Days') {
        const startDate = new Date(); startDate.setDate(startDate.getDate() - 30); dateMatch = txDate >= startDate;
      } else if (selectedDateRange === 'Last 90 Days') {
        const startDate = new Date(); startDate.setDate(startDate.getDate() - 90); dateMatch = txDate >= startDate;
      }

      // Type Filter
      const typeMatch = selectedType === 'All Types' || tx.type === selectedType;

      // Country Filter
      const countryMatch = selectedCountry === 'All Countries' || tx.location === selectedCountry;

      // Status Filter
      const statusMatch = selectedStatus === 'All Statuses' || tx.status === selectedStatus;

      // Search Term Filter
      const matchesSearch = searchTerm === '' ||
                         tx.partner.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         tx.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         tx.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         tx.amount.toString().includes(searchTerm) ||
                         tx.currency.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (tx.location && tx.location.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (tx.invoiceNumber && tx.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()));

      return dateMatch && typeMatch && countryMatch && statusMatch && matchesSearch;
    });
  }, [transactions, searchTerm, selectedDateRange, selectedType, selectedCountry, selectedStatus, customStartDate, customEndDate]);

  // Calculate balance metrics
  const balanceMetrics = React.useMemo(() => {
    // Initialize metrics
    let outstandingBalance = 0;
    let totalBilled = 0;
    let totalPaid = 0;
    let overdueAmount = 0;
    
    // Exchange rates (simplified for demo)
    const exchangeRates: { [currency: string]: number } = {
      'AED': 0.27, 'SAR': 0.27, 'QAR': 0.27, 'KWD': 3.27, 'BHD': 2.65, 'USD': 1
    };
    
    // Process transactions
    filteredTransactions.forEach(tx => {
      const amount = tx.amount * (exchangeRates[tx.currency] || 1);
      const category = tx.category || getTransactionCategoryBase(tx);
      
      if (category === 'Invoice') {
        totalBilled += Math.abs(amount);
        if (tx.status === 'Overdue') {
          overdueAmount += Math.abs(amount);
        }
        if (tx.status !== 'Paid') {
          outstandingBalance += Math.abs(amount);
        }
      } else if (category === 'Payment') {
        totalPaid += Math.abs(amount);
      }
    });
    
    // Last month metrics for comparison
    const lastMonthBilled = totalBilled * 0.9; // 10% growth
    const lastMonthPaid = totalPaid * 0.88; // 12% growth
    
    return {
      outstandingBalance,
      totalBilled,
      totalPaid,
      overdueAmount,
      billedChange: totalBilled - lastMonthBilled,
      paidChange: totalPaid - lastMonthPaid
    };
  }, [filteredTransactions]);

  // Handler functions
  const handleCustomDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setCustomStartDate(start);
    setCustomEndDate(end);
  };

  const handleDateRangeChange = (value: string) => {
    setSelectedDateRange(value);
    if (value === 'Custom date') {
      setIsDatePickerVisible(true);
    } else {
      setIsDatePickerVisible(false);
      setCustomStartDate(null);
      setCustomEndDate(null);
    }
  };

  const handleCopyID = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch (err) {
      console.error('Failed to copy ID: ', err);
    }
  };

  const handleRowClick = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setTimeout(() => setIsPanelVisible(true), 10);
  };

  const handleCloseDetailsPanel = () => {
    setIsPanelVisible(false);
    setTimeout(() => setSelectedTransaction(null), 200);
  };

  // Count metrics
  const pendingInvoices = filteredTransactions.filter(tx => tx.category === 'Invoice' && tx.status === 'Pending').length;
  const overdueInvoices = filteredTransactions.filter(tx => tx.category === 'Invoice' && tx.status === 'Overdue').length;

  return (
    <MainLayout>
      <div style={{ padding: '24px' }}>
        {/* Back Button */}
        <button
          onClick={() => window.history.back()}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            marginBottom: '18px',
            cursor: 'pointer',
            color: '#64748b',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            transition: 'color 0.2s ease'
          }}
        >
          <ArrowLeft size={16} style={{ marginRight: '6px' }} />
          Back to Wallet Dashboard
        </button>

        {/* Header Area */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ marginBottom: '20px' }}>
            <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 600, color: '#111827', lineHeight: '1.2' }}>Ads Postpaid Wallet</h1>
            <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '14px' }}>Manage invoices and payments for the Ads Postpaid product.</p>
          </div>
        </div>

        {/* Filters Row */}
        <div style={{ padding: '16px 0', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' }}>
          {/* Filters */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <DropdownFilter label="Date" options={dateRangeOptions} selectedValue={selectedDateRange} onChange={handleDateRangeChange} />
            {isDatePickerVisible && (
              <div style={{ marginLeft: '-6px' }}>
                <RangeDatePicker startDate={customStartDate} endDate={customEndDate} onChange={handleCustomDateChange} />
              </div>
            )}
            <DropdownFilter label="Type" options={typeOptions} selectedValue={selectedType} onChange={setSelectedType} />
            <DropdownFilter label="Country" options={countryOptions} selectedValue={selectedCountry} onChange={setSelectedCountry} />
            <DropdownFilter label="Status" options={statusOptions} selectedValue={selectedStatus} onChange={setSelectedStatus} />
          </div>
          {/* Search */}
          <div style={{ marginLeft: 'auto' }}>
            <input
              type="search"
              placeholder="Search partner, invoice #, amount..."
              style={{
                padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px',
                minWidth: '200px'
              }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Dashboard Grid */}
        <div style={{ marginBottom: '32px' }}>
          {/* Metrics Row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: '16px',
            marginBottom: '64px'
          }}>
            <MainBalanceWidget 
              title="Outstanding Balance" 
              totalBalance={balanceMetrics.outstandingBalance} 
              change={balanceMetrics.billedChange * 0.2} 
              currency="USD" 
              icon={<CreditCard />}
              isNegative={true}
            />

            <MetricWidget 
              title="Total Billed"
              value={formatCurrency(balanceMetrics.totalBilled, 'USD')} 
              icon={<File />} 
              secondaryValue={`+${(balanceMetrics.billedChange / (balanceMetrics.totalBilled - balanceMetrics.billedChange) * 100).toFixed(1)}% from last month`}
              secondaryColor="#ef4444"
              bgColor="#fee2e2"
              iconColor="#ef4444"
            />
            
            <MetricWidget 
              title="Total Paid" 
              value={formatCurrency(balanceMetrics.totalPaid, 'USD')} 
              icon={<CheckCircle />} 
              secondaryValue={`+${(balanceMetrics.paidChange / (balanceMetrics.totalPaid - balanceMetrics.paidChange) * 100).toFixed(1)}% from last month`} 
            />

            <MetricWidget 
              title="Pending Invoices" 
              value={pendingInvoices.toString()} 
              icon={<Clock />} 
              secondaryValue={overdueInvoices > 0 ? `${overdueInvoices} overdue` : "No overdue invoices"} 
              secondaryColor={overdueInvoices > 0 ? "#ef4444" : "#10b981"}
              bgColor="#fef3c7" 
              iconColor="#d97706" 
            />
            
            <MetricWidget 
              title="Overdue Amount" 
              value={formatCurrency(balanceMetrics.overdueAmount, 'USD')} 
              icon={<AlertTriangle />} 
              bgColor="#fee2e2"
              iconColor="#ef4444"
              secondaryValue={balanceMetrics.overdueAmount > 0 ? "Requires immediate attention" : "No overdue amounts"}
              secondaryColor={balanceMetrics.overdueAmount > 0 ? "#ef4444" : "#10b981"}
            />
          </div>
        </div>

        {/* Transaction History Section */}
        <section style={{ backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden' }}>
          {/* Table */}
          {filteredTransactions.length > 0 ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead style={{ backgroundColor: '#f9fafb' }}>
                <tr style={{ borderBottom: '1px solid #e5e7eb', textAlign: 'left', color: '#6b7280' }}>
                  <th style={{ fontWeight: 500, padding: '12px 16px', textAlign: 'center' }}>ID</th>
                  <th style={{ fontWeight: 500, padding: '12px 16px' }}>Date</th>
                  <th style={{ fontWeight: 500, padding: '12px 16px' }}>Partner</th>
                  <th style={{ fontWeight: 500, padding: '12px 16px' }}>Country</th>
                  <th style={{ fontWeight: 500, padding: '12px 16px' }}>Category</th>
                  <th style={{ fontWeight: 500, padding: '12px 16px' }}>Type</th>
                  <th style={{ fontWeight: 500, padding: '12px 16px', textAlign: 'right' }}>Amount</th>
                  <th style={{ fontWeight: 500, padding: '12px 16px', textAlign: 'center' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((tx, index) => {
                  const category = tx.category || getTransactionCategoryBase(tx);
                  return (
                  <tr
                    key={tx.id}
                    data-transaction-row="true"
                    style={{
                      borderBottom: '1px solid #f3f4f6', cursor: 'pointer',
                      backgroundColor: hoveredRowIndex === index ? '#f9fafb' : 'transparent',
                      transition: 'background-color 0.1s ease-in-out', height: '35px'
                    }}
                    onMouseEnter={() => setHoveredRowIndex(index)}
                    onMouseLeave={() => setHoveredRowIndex(null)}
                    onClick={() => handleRowClick(tx)}
                  >
                    {/* ID Cell */}
                    <td style={{ padding: '7px 16px', width: '60px', textAlign: 'center' }}>
                      <div
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', height: '100%', position: 'relative' }}
                        onClick={(e) => { e.stopPropagation(); handleCopyID(tx.id); }}
                        title={copiedId === tx.id ? 'ID copied' : `Click to copy ID: ${tx.id}`}
                        onMouseEnter={() => setHoveredIdIcon(tx.id)}
                        onMouseLeave={() => setHoveredIdIcon(null)}
                      >
                        {copiedId === tx.id ? <CheckIcon style={{ color: '#10b981' }} size={16} />
                          : hoveredIdIcon === tx.id ? <CopyIcon style={{ color: '#6b7280' }} size={14} />
                          : <HashIcon style={{ color: '#9ca3af' }} size={14} />}
                      </div>
                    </td>
                    {/* Date Cell */}
                    <td style={{ padding: '7px 16px', color: '#374151', whiteSpace: 'nowrap', width: '140px' }}>
                      {`${formatDateTime(tx.transactionDate).date}`}
                    </td>
                    {/* Partner Cell */}
                    <td style={{ padding: '7px 16px' }}>
                      <div style={{ fontWeight: 400, color: '#111827' }}>{tx.partner}</div>
                    </td>
                    {/* Country Cell */}
                    <td style={{ padding: '7px 16px', color: '#374151', width: '80px' }}>{tx.location || 'N/A'}</td>
                    {/* Category Cell */}
                    <td style={{ padding: '7px 16px' }}>
                      <CategoryBadge category={category} />
                    </td>
                    {/* Type Cell */}
                    <td style={{ padding: '7px 16px', fontWeight: 400, color: '#111827' }}>
                      {category === 'Invoice' && tx.invoiceNumber ? 
                        <div>{tx.type} <span style={{ color: '#6b7280', fontSize: '11px' }}>({tx.invoiceNumber})</span></div> : 
                        tx.type}
                    </td>
                    {/* Amount Cell */}
                    <td style={{ padding: '7px 16px', fontWeight: 500, color: '#111827', textAlign: 'right' }}>
                      {formatCurrency(Math.abs(tx.amount), tx.currency)}
                    </td>
                    {/* Status Cell */}
                    <td style={{ padding: '7px 16px', textAlign: 'center' }}>
                      <StatusBadge status={tx.status} />
                    </td>
                  </tr>
                )})}
              </tbody>
            </table>
          ) : (
            // Empty State
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#6b7280', borderTop: '1px solid #e5e7eb', margin: '0 24px' }}>
              <FileText size={48} style={{ color: '#d1d5db', marginBottom: '16px' }} />
              <p style={{ fontSize: '16px', fontWeight: 500, color: '#111827', marginBottom: '4px' }}>No Ads Postpaid Transactions Found</p>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>Try adjusting your filter criteria.</p>
            </div>
          )}
        </section>
      </div>

      {/* Render Transaction Details Panel */}
      {selectedTransaction && (
        <TransactionDetailsPanel
          transaction={selectedTransaction}
          onClose={handleCloseDetailsPanel}
          isVisible={isPanelVisible}
        />
      )}
    </MainLayout>
  );
};

export default AdsPostpaidWalletPage;
